import React, { useState, useEffect } from 'react';
import { 
  Title, 
  Card, 
  Table, 
  Badge, 
  Group, 
  Text, 
  Switch, 
  Button, 
  Stack, 
  ActionIcon, 
  Tooltip,
  LoadingOverlay,
  TagsInput,
  Select,
  Flex,
  Box
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { HiOutlineRefresh, HiOutlineSave, HiOutlineClock } from 'react-icons/hi';
import axios from 'axios';
import dayjs from 'dayjs';

const CronManagement = () => {
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, logsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/monitor/cron-configs`),
        axios.get(`${process.env.REACT_APP_API_URL}/monitor/cron-logs`)
      ]);
      setConfigs(configRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'No se pudo cargar la información de las crons',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateConfig = async (taskName, data) => {
    setSaving(true);
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/monitor/cron-configs/${taskName}`, data);
      notifications.show({
        title: 'Éxito',
        message: 'Configuración actualizada correctamente',
        color: 'green'
      });
      fetchData();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'No se pudo actualizar la configuración',
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="w-full pt-2 px-2 pb-xl">
      <Flex justify="space-between" align="center" mb="lg" px="md">
        <Box>
          <Title order={1} c="dark.5">Gestión de Tareas Programadas</Title>
          <Text c="dimmed" size="sm" fw={500}>Configura el envío de reportes y procesos automáticos</Text>
        </Box>
        <ActionIcon variant="light" color="yellow" size="lg" onClick={fetchData} loading={loading}>
          <HiOutlineRefresh size={20} />
        </ActionIcon>
      </Flex>

      <Stack px="md" gap="xl">
        {/* Configurations Section */}
        <Card withBorder radius="lg" p="xl" shadow="sm" style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading || saving} />
          <Title order={4} mb="md" tt="uppercase" className="tracking-widest">Configuración de Reportes</Title>
          <Box className="overflow-x-auto">
            <Table verticalSpacing="md" highlightOnHover>
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">TAREA</Text></Table.Th>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">FRECUENCIA</Text></Table.Th>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">HORARIO (MX)</Text></Table.Th>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">DESTINATARIOS</Text></Table.Th>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">ESTADO</Text></Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {configs.map((config) => (
                  <CronConfigRow 
                    key={config._id} 
                    config={config} 
                    onSave={(data) => handleUpdateConfig(config.taskName, data)} 
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>

        {/* Recent Activity Section */}
        <Card withBorder radius="lg" p="xl" shadow="sm">
          <Title order={4} mb="md" tt="uppercase" className="tracking-widest">Actividad Reciente</Title>
          <Box className="overflow-x-auto">
            <Table verticalSpacing="xs" striped highlightOnHover>
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">ESTADO</Text></Table.Th>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">TAREA</Text></Table.Th>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">EJECUTADO</Text></Table.Th>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">RESUMEN</Text></Table.Th>
                  <Table.Th><Text size="xs" fw={800} c="dimmed">DURACIÓN</Text></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {logs.length > 0 ? logs.map((log) => (
                  <CronLogItem key={log._id} log={log} />
                )) : (
                  <Table.Tr>
                    <Table.Td colSpan={5} align="center" py="xl">
                      <Text size="sm" c="dimmed">No hay actividad reciente para mostrar.</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>
      </Stack>
    </Box>
  );
};

const CronConfigRow = ({ config, onSave }) => {
  const parseCron = (cron) => {
    if (!cron) return { freq: 'DAILY', hour: '12', minute: '00' };
    const parts = cron.split(' ');
    const minute = parts[1] || '00';
    const hour = parts[2] || '12';
    const dayOfMonth = parts[3];
    const dayOfWeek = parts[5];

    let freq = 'DAILY';
    if (dayOfWeek === '0') freq = 'WEEKLY';
    if (dayOfMonth === '1') freq = 'MONTHLY';

    return { freq, hour: hour.padStart(2, '0'), minute: minute.padStart(2, '0') };
  };

  const initial = parseCron(config.schedule);
  
  const [frequency, setFrequency] = useState(initial.freq);
  const [hour, setHour] = useState(initial.hour);
  const [minute, setMinute] = useState(initial.minute);
  const [recipients, setRecipients] = useState(config.recipients || []);
  const [isActive, setIsActive] = useState(config.isActive);
  const [contractType, setContractType] = useState(config.contractType || 'Todos');

  const hoursList = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  const minutesList = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  const handleSave = () => {
    let schedule = `0 ${parseInt(minute)} ${parseInt(hour)} * * *`;
    if (frequency === 'WEEKLY') schedule = `0 ${parseInt(minute)} ${parseInt(hour)} * * 0`;
    if (frequency === 'MONTHLY') schedule = `0 ${parseInt(minute)} ${parseInt(hour)} 1 * *`;

    const payload = { 
      schedule, 
      recipients, 
      isActive, 
      taskName: config.taskName, 
      jobName: config.jobName 
    };

    if (config.taskName === 'billing-report') {
      payload.contractType = contractType;
    }

    onSave(payload);
  };

  return (
    <Table.Tr>
      <Table.Td>
        <Stack gap={4}>
          <Text fw={700} size="sm">
            {config.taskName === 'billing-report' ? 'Reporte de Facturación' : config.taskName}
          </Text>
          {config.taskName === 'billing-report' && (
            <Select
              label={<Text size="xs" c="dimmed" fw={600}>Filtro de Contrato</Text>}
              size="xs"
              variant="filled"
              radius="md"
              w={130}
              value={contractType}
              onChange={setContractType}
              data={[
                { value: 'Prepago', label: 'Prepago' },
                { value: 'Postpago', label: 'Postpago' },
                { value: 'Todos', label: 'Todos' },
              ]}
            />
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Select
          size="xs" variant="filled" radius="md" w={120}
          value={frequency} onChange={setFrequency}
          data={[
            { value: 'DAILY', label: 'Diario' },
            { value: 'WEEKLY', label: 'Semanal (Dom)' },
            { value: 'MONTHLY', label: 'Mensual (Día 1)' },
          ]}
        />
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <Select
            size="xs" variant="filled" radius="md" w={55}
            value={hour} onChange={setHour}
            data={hoursList}
          />
          <Text size="xs" fw={700}>:</Text>
          <Select
            size="xs" variant="filled" radius="md" w={55}
            value={minute} onChange={setMinute}
            data={minutesList}
          />
          <HiOutlineClock size={14} className="text-gray-400" />
        </Group>
      </Table.Td>
      <Table.Td style={{ minWidth: 250 }}>
        <TagsInput 
          size="xs" variant="filled" radius="md"
          placeholder="Emails" value={recipients} onChange={setRecipients}
          splitChars={[',', ' ']}
        />
      </Table.Td>
      <Table.Td>
        <Switch 
          checked={isActive} 
          onChange={(e) => setIsActive(e.currentTarget.checked)}
          color="yellow" size="sm"
        />
      </Table.Td>
      <Table.Td>
        <Tooltip label="Guardar cambios">
          <ActionIcon 
            variant="filled" color="yellow" size="md" radius="md"
            onClick={handleSave}
          >
            <HiOutlineSave size={16} />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  );
};

const CronLogItem = ({ log }) => {
  const isSuccess = log.status === 'success';
  const isFailed = log.status === 'failed';
  
  return (
    <Table.Tr>
      <Table.Td>
        <Badge color={isSuccess ? 'green' : isFailed ? 'red' : 'blue'} size="sm" variant="light" radius="sm">
          {log.status.toUpperCase()}
        </Badge>
      </Table.Td>
      <Table.Td><Text size="sm" fw={700}>{log.taskName === 'billing-report' ? 'Reporte de Facturación' : log.taskName}</Text></Table.Td>
      <Table.Td><Text size="xs" fw={500}>{dayjs(log.startedAt).format('DD/MM/YYYY HH:mm:ss')}</Text></Table.Td>
      <Table.Td>
        <Tooltip label={log.error || log.result}>
          <Text size="sm" c="dark.3" truncate="end" style={{ maxWidth: 250 }}>
            {log.summary || (log.status === 'running' ? 'En progreso...' : 'Sin detalles')}
          </Text>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Text size="xs" fw={700}>
          {log.finishedAt ? `${((new Date(log.finishedAt) - new Date(log.startedAt)) / 1000).toFixed(2)}s` : '-'}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
};

export default CronManagement;
